import { Injectable, PLATFORM_ID, inject, signal } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';
import { filter } from 'rxjs/operators';

export interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[];
  readonly userChoice: Promise<{
    outcome: 'accepted' | 'dismissed';
    platform: string;
  }>;
  prompt(): Promise<void>;
}

@Injectable({
  providedIn: 'root',
})
export class PwaService {
  private readonly platformId = inject(PLATFORM_ID);
  private readonly swUpdate = inject(SwUpdate, { optional: true });

  private deferredPrompt: BeforeInstallPromptEvent | null = null;
  private readonly STORAGE_DISMISSED_KEY = 'gymo_pwa_install_dismissed_until';

  // Signals for reactive UI state
  readonly canInstall = signal<boolean>(false);
  readonly isInstalled = signal<boolean>(false);
  readonly isStandalone = signal<boolean>(false);
  readonly isIOS = signal<boolean>(false);
  readonly showInstallToast = signal<boolean>(false);
  readonly showIOSInstructions = signal<boolean>(false);
  readonly hasUpdate = signal<boolean>(false);

  constructor() {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    this.checkStandaloneMode();
    this.detectIOS();
    this.initInstallPromptListener();
    this.initAppInstalledListener();
    this.initSwUpdateListener();
  }

  /**
   * Check if the application is already running in standalone (installed) mode
   */
  private checkStandaloneMode(): void {
    const isStandaloneDisplay = window.matchMedia('(display-mode: standalone)').matches;
    const isStandaloneNavigator = (window.navigator as unknown as { standalone?: boolean }).standalone === true;

    if (isStandaloneDisplay || isStandaloneNavigator) {
      this.isStandalone.set(true);
      this.isInstalled.set(true);
      this.canInstall.set(false);
      this.showInstallToast.set(false);
    }
  }

  /**
   * Detect if the device is iOS (Safari doesn't support beforeinstallprompt)
   */
  private detectIOS(): void {
    const ua = window.navigator.userAgent.toLowerCase();
    const isIosDevice = /iphone|ipad|ipod/.test(ua) || 
      (window.navigator.platform === 'MacIntel' && window.navigator.maxTouchPoints > 1);

    if (isIosDevice && !this.isStandalone()) {
      this.isIOS.set(true);
      // For iOS, if not dismissed recently, we can show the banner with iOS instructions
      if (!this.isDismissedRecently()) {
        this.showInstallToast.set(true);
      }
    }
  }

  /**
   * Listen to the browser's beforeinstallprompt event (Chromium browsers)
   */
  private initInstallPromptListener(): void {
    window.addEventListener('beforeinstallprompt', (e: Event) => {
      // Prevent the mini-infobar from appearing automatically
      e.preventDefault();

      // Store the event so it can be triggered later
      this.deferredPrompt = e as BeforeInstallPromptEvent;
      this.canInstall.set(true);

      // Show toast if not recently dismissed
      if (!this.isDismissedRecently() && !this.isStandalone()) {
        this.showInstallToast.set(true);
      }
    });
  }

  /**
   * Listen to appinstalled event to know when the app has been installed
   */
  private initAppInstalledListener(): void {
    window.addEventListener('appinstalled', () => {
      this.isInstalled.set(true);
      this.canInstall.set(false);
      this.showInstallToast.set(false);
      this.deferredPrompt = null;
      localStorage.removeItem(this.STORAGE_DISMISSED_KEY);
    });
  }

  /**
   * Listen to Service Worker updates
   */
  private initSwUpdateListener(): void {
    if (this.swUpdate?.isEnabled) {
      this.swUpdate.versionUpdates
        .pipe(filter((evt): evt is VersionReadyEvent => evt.type === 'VERSION_READY'))
        .subscribe(() => {
          this.hasUpdate.set(true);
        });
    }
  }

  /**
   * Trigger the native PWA install prompt
   */
  async installPwa(): Promise<boolean> {
    if (!this.deferredPrompt) {
      if (this.isIOS()) {
        this.showIOSInstructions.set(true);
      }
      return false;
    }

    try {
      await this.deferredPrompt.prompt();
      const choiceResult = await this.deferredPrompt.userChoice;

      if (choiceResult.outcome === 'accepted') {
        this.isInstalled.set(true);
        this.canInstall.set(false);
        this.showInstallToast.set(false);
        this.deferredPrompt = null;
        return true;
      } else {
        this.dismissPrompt(1);
        return false;
      }
    } catch (err) {
      console.error('Error during PWA installation:', err);
      return false;
    }
  }

  /**
   * Dismiss the install prompt and save a cooldown period in localStorage
   * @param hour Number of hours before showing the prompt again (default 24h)
   */
  dismissPrompt(hour: number = 1): void {
    this.showInstallToast.set(false);
    this.showIOSInstructions.set(false);
    const expiryTimestamp = Date.now() + hour * 60 * 60 * 1000;
    localStorage.setItem(this.STORAGE_DISMISSED_KEY, expiryTimestamp.toString());
  }

  /**
   * Check whether the user has dismissed the toast recently
   */
  private isDismissedRecently(): boolean {
    const dismissedUntil = localStorage.getItem(this.STORAGE_DISMISSED_KEY);
    if (!dismissedUntil) return false;

    const timestamp = parseInt(dismissedUntil, 10);
    if (isNaN(timestamp)) return false;

    return Date.now() < timestamp;
  }

  /**
   * Manually open the install prompt (e.g. from header button or settings)
   */
  openInstallPrompt(): void {
    if (this.isStandalone()) return;
    this.showInstallToast.set(true);
  }

  /**
   * Toggle iOS instructions modal/view
   */
  toggleIOSInstructions(): void {
    this.showIOSInstructions.update(val => !val);
  }

  /**
   * Reload the page to activate a pending Service Worker update
   */
  reloadForUpdate(): void {
    if (this.swUpdate?.isEnabled) {
      this.swUpdate.activateUpdate().then(() => {
        window.location.reload();
      });
    } else {
      window.location.reload();
    }
  }
}
